import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useToast } from "../../contexts/ToastContext";
import { useUserAuth } from "../../contexts/UserAuthContext";
import api from "../../utils/api/axiosInstance";
import getImageUrl from "../../utils/imageUtils";
import useDebounce from "../../hooks/useDebounce";
import UserCard from "../../components/users/UserCard";
import PageSeo from "../../components/common/PageSeo";
import {
  FaSearch,
  FaUser,
  FaUsers,
  FaSpinner,
  FaUserPlus,
  FaUserCheck,
} from "react-icons/fa";
import styles from "../../styles/pages/users/UserSearchPage.module.css";

const UserSearchPage = () => {
  const { user: currentUser } = useUserAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchInput, setSearchInput] = useState(query);

  // Suggestions/autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [displayQuery, setDisplayQuery] = useState(query); // For autofill during keyboard navigation

  // Refs for managing focus and outside clicks
  const searchWrapperRef = useRef(null);
  const suggestionRefs = useRef([]);
  const isKeyboardNavigating = useRef(false);
  const originalQueryRef = useRef("");

  // Debounce the search input for suggestions - shorter delay for responsiveness
  const debouncedSearchInput = useDebounce(searchInput, 150);

  const fetchSuggestedUsers = useCallback(async () => {
    if (!currentUser) {
      setSuggestedUsers([]);
      setSuggestionsLoading(false);
      return;
    }

    setSuggestionsLoading(true);
    try {
      const res = await api.get("/users/discover", {
        params: { limit: 20 },
      });
      setSuggestedUsers(res.data.users || []);
    } catch (err) {
      console.error("Error fetching suggested users:", err);
      setSuggestedUsers([]);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchSuggestedUsers();
  }, [fetchSuggestedUsers]);

  const handleFollowSuggestedUser = async (userId) => {
    try {
      const res = await api.post(`/users/${userId}/follow`);
      setSuggestedUsers((prev) =>
        prev.map((u) =>
          u._id === userId ? { ...u, isFollowing: res.data.isFollowing } : u,
        ),
      );
    } catch (err) {
      console.error("Error following user:", err);
      showToast({
        message: err.response?.data?.message || "Failed to follow user",
        type: "error",
      });
    }
  };

  const searchUsers = useCallback(
    async (searchQuery, pageNum = 1) => {
      if (!searchQuery.trim()) {
        setUsers([]);
        setHasMore(false);
        return;
      }

      setLoading(true);
      setLoadError(null);
      try {
        const res = await api.get("/users/search", {
          params: {
            q: searchQuery.trim(),
            page: pageNum,
            limit: 20,
          },
        });

        const newUsers = res.data.users || [];

        if (pageNum === 1) {
          setUsers(newUsers);
        } else {
          setUsers((prev) => [...prev, ...newUsers]);
        }

        setHasMore(res.data.pagination?.hasMore || false);
        setPage(pageNum);
      } catch (err) {
        console.error("Error searching users:", err);
        showToast({
          message: err.response?.data?.message || "Failed to search users",
          type: "error",
        });
        setUsers([]);
        setLoadError("We couldn't load search results.");
      } finally {
        setLoading(false);
      }
    },
    [showToast],
  );

  // Fetch suggestions when debounced search input changes
  useEffect(() => {
    let cancelled = false;

    const fetchSuggestions = async () => {
      // Don't fetch if we're just navigating with keyboard
      if (isKeyboardNavigating.current) {
        return;
      }

      if (!debouncedSearchInput.trim() || debouncedSearchInput.length < 1) {
        if (!cancelled) {
          setSuggestions([]);
          setShowSuggestions(false);
          setDisplayQuery("");
        }
        return;
      }

      try {
        const res = await api.get("/users/search", {
          params: {
            q: debouncedSearchInput.trim(),
            page: 1,
            limit: 8, // Smaller limit for suggestions
          },
        });

        if (!cancelled) {
          let results = res.data.users || [];

          // Filter to only show users whose names start with the query
          const queryLower = debouncedSearchInput.toLowerCase().trim();
          const filteredResults = results.filter(user => {
            const firstName = (user.firstName || '').toLowerCase();
            const lastName = (user.lastName || '').toLowerCase();
            const username = (user.username || '').toLowerCase();

            return firstName.startsWith(queryLower) ||
                   lastName.startsWith(queryLower) ||
                   username.startsWith(queryLower);
          });

          // Sort the filtered results
          const sortedResults = filteredResults.sort((a, b) => {
            const aFirstName = (a.firstName || '').toLowerCase();
            const aLastName = (a.lastName || '').toLowerCase();
            const aUsername = (a.username || '').toLowerCase();
            const bFirstName = (b.firstName || '').toLowerCase();
            const bLastName = (b.lastName || '').toLowerCase();
            const bUsername = (b.username || '').toLowerCase();

            // Prioritize: firstName > lastName > username
            if (aFirstName.startsWith(queryLower) && !bFirstName.startsWith(queryLower)) return -1;
            if (!aFirstName.startsWith(queryLower) && bFirstName.startsWith(queryLower)) return 1;

            if (aFirstName.startsWith(queryLower) && bFirstName.startsWith(queryLower)) {
              return aFirstName.localeCompare(bFirstName);
            }

            if (aLastName.startsWith(queryLower) && !bLastName.startsWith(queryLower)) return -1;
            if (!aLastName.startsWith(queryLower) && bLastName.startsWith(queryLower)) return 1;

            if (aLastName.startsWith(queryLower) && bLastName.startsWith(queryLower)) {
              return aLastName.localeCompare(bLastName);
            }

            if (aUsername.startsWith(queryLower) && !bUsername.startsWith(queryLower)) return -1;
            if (!aUsername.startsWith(queryLower) && bUsername.startsWith(queryLower)) return 1;

            if (aUsername.startsWith(queryLower) && bUsername.startsWith(queryLower)) {
              return aUsername.localeCompare(bUsername);
            }

            return 0;
          });

          setSuggestions(sortedResults);
          const isCommittedSearch =
            query.trim() &&
            debouncedSearchInput.trim().toLowerCase() ===
              query.trim().toLowerCase();
          setShowSuggestions(!isCommittedSearch && sortedResults.length > 0);
          setHighlightedIndex(-1); // Reset highlighted index when suggestions change
          // Only update displayQuery if we're not currently navigating
          if (!isKeyboardNavigating.current) {
            setDisplayQuery(debouncedSearchInput); // Sync display query with search input
            originalQueryRef.current = debouncedSearchInput; // Store as original
          }
        }
      } catch (err) {
        console.error("Error fetching user suggestions:", err);
        if (!cancelled) {
          setSuggestions([]);
          setShowSuggestions(false);
          setDisplayQuery("");
        }
      }
    };

    fetchSuggestions();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearchInput, query]);

  // Handle outside clicks to close suggestions
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        // Restore original query when clicking outside
        setDisplayQuery(searchInput);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [searchInput]);

  // Fetch full results when query changes
  useEffect(() => {
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    if (query) {
      searchUsers(query, 1);
    } else {
      setUsers([]);
    }
  }, [query, searchUsers]);

  const handleSearch = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const trimmedQuery = searchInput.trim();
    if (trimmedQuery) {
      setSearchParams({ q: trimmedQuery });
    }
    // Hide suggestions when search is performed
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore && query) {
      searchUsers(query, page + 1);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (user) => {
    window.location.assign(`/user/${user._id}`);
    setSuggestions([]);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === "Enter") {
        handleSearch();
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        // Store original query when starting navigation
        if (highlightedIndex === -1) {
          originalQueryRef.current = searchInput;
        }
        isKeyboardNavigating.current = true;
        setHighlightedIndex((prev) => {
          const nextIndex = prev < suggestions.length - 1 ? prev + 1 : prev;
          // Scroll into view if needed
          if (suggestionRefs.current[nextIndex]) {
            suggestionRefs.current[nextIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          // Update display query (autofill) but don't trigger search
          if (nextIndex >= 0 && suggestions[nextIndex]?.firstName) {
            setDisplayQuery(`${suggestions[nextIndex].firstName} ${suggestions[nextIndex].lastName || ''}`.trim());
          }
          return nextIndex;
        });
        // Reset flag after state update
        setTimeout(() => {
          isKeyboardNavigating.current = false;
        }, 0);
        break;

      case "ArrowUp":
        e.preventDefault();
        isKeyboardNavigating.current = true;
        setHighlightedIndex((prev) => {
          const nextIndex = prev > 0 ? prev - 1 : -1;
          // Scroll into view if needed
          if (nextIndex >= 0 && suggestionRefs.current[nextIndex]) {
            suggestionRefs.current[nextIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          // Update display query (autofill) or restore original if at top
          if (nextIndex >= 0 && suggestions[nextIndex]?.firstName) {
            setDisplayQuery(`${suggestions[nextIndex].firstName} ${suggestions[nextIndex].lastName || ''}`.trim());
          } else if (nextIndex === -1) {
            // Reset to original query when at top
            setDisplayQuery(originalQueryRef.current || debouncedSearchInput);
          }
          return nextIndex;
        });
        // Reset flag after state update
        setTimeout(() => {
          isKeyboardNavigating.current = false;
        }, 0);
        break;

      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          // Use the highlighted suggestion
          handleSuggestionClick(suggestions[highlightedIndex]);
        } else {
          // Use the current display query for search
          handleSearch();
        }
        // Hide suggestions after action
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;

      case "Escape":
        e.preventDefault();
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        // Restore original query
        setDisplayQuery(originalQueryRef.current || searchInput);
        break;

      default:
        // Reset highlighted index when typing
        if (e.key.length === 1) {
          setHighlightedIndex(-1);
        }
        break;
    }
  };

  const isSearching = Boolean(query.trim());

  return (
    <div className="pageContainer">
      <PageSeo
        title="Find People"
        description="Search for and connect with people on Simpary."
        pathname="/users/search"
        noIndex
      />
      <div className="innerContainer">
        <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          <FaUsers /> Connect
        </h1>
        <p className={styles.subtitle}>
          Search for someone specific, or browse suggested people below.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch();
        }}
        className={styles.searchForm}
      >
        <div className={styles.searchInputContainer} ref={searchWrapperRef}>
          <FaSearch className={styles.searchIcon} />
          <input
            type="text"
            value={displayQuery || searchInput}
            onChange={(e) => {
              const newValue = e.target.value;
              setSearchInput(newValue);
              setDisplayQuery(newValue);
              setHighlightedIndex(-1);
              if (newValue.trim().length >= 1) {
                setShowSuggestions(true);
              } else {
                setShowSuggestions(false);
              }
            }}
            onFocus={() => {
              if (suggestions.length > 0) {
                setShowSuggestions(true);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search by name, username, or bio..."
            className={styles.searchInput}
          />
          <button
            type="button"
            onClick={handleSearch}
            className={styles.searchButton}
            disabled={loading}
          >
            {loading ? <FaSpinner className={styles.spinner} /> : "Search"}
          </button>
        </div>

        {showSuggestions && suggestions.length > 0 && (
          <div className={styles.autocompleteWrapper}>
            <ul className={styles.autocompleteList}>
              {suggestions.map((user, index) => (
                <li
                  key={user._id}
                  ref={(el) => (suggestionRefs.current[index] = el)}
                  className={`${styles.autocompleteItem} ${
                    index === highlightedIndex
                      ? styles.autocompleteItemHighlighted
                      : ""
                  }`}
                  onClick={() => handleSuggestionClick(user)}
                  onMouseEnter={() => {
                    setHighlightedIndex(index);
                    if (highlightedIndex === -1) {
                      originalQueryRef.current = searchInput;
                    }
                    if (user?.firstName) {
                      setDisplayQuery(`${user.firstName} ${user.lastName || ''}`.trim());
                    }
                  }}
                >
                  <div className={styles.suggestionContent}>
                    <div className={styles.suggestionAvatar}>
                      {user.avatar ? (
                        <img
                          src={getImageUrl(user.avatar)}
                          alt={`${user.firstName} ${user.lastName}`}
                        />
                      ) : (
                        <div className={styles.avatarPlaceholder}>
                          {user.firstName?.[0] || user.username?.[0] || "U"}
                        </div>
                      )}
                    </div>
                    <div className={styles.suggestionInfo}>
                      <span className={styles.suggestionName}>
                        {user.firstName} {user.lastName || ''}
                      </span>
                      {user.username && (
                        <span className={styles.suggestionUsername}>
                          @{user.username}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
          </ul>
          </div>
        )}
      </form>

      {isSearching ? (
        <>
          {loading && users.length === 0 ? (
            <div className={styles.loadingState}>
              <FaSpinner className={styles.spinner} />
              <p>Searching users...</p>
            </div>
          ) : users.length > 0 ? (
            <>
              <div className={styles.resultsHeader}>
                <h2 className={styles.sectionTitle}>Search results</h2>
                <p className={styles.resultsCount}>
                  Found {users.length} {users.length === 1 ? "user" : "users"}
                </p>
              </div>
              <div className={styles.usersGrid}>
                {users.map((user) => (
                  <UserCard key={user._id} user={user} />
                ))}
              </div>
              {hasMore && (
                <div className={styles.loadMoreContainer}>
                  <button
                    onClick={handleLoadMore}
                    className={styles.loadMoreButton}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <FaSpinner className={styles.spinner} /> Loading...
                      </>
                    ) : (
                      "Load More"
                    )}
                  </button>
                </div>
              )}
            </>
          ) : loadError ? (
            <div className={styles.emptyState}>
              <FaUser className={styles.emptyIcon} />
              <h3>Couldn't load results</h3>
              <p>{loadError}</p>
              <button
                className={styles.loadMoreButton}
                onClick={() => searchUsers(query, 1)}
              >
                Retry
              </button>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <FaUser className={styles.emptyIcon} />
              <h3>No users found</h3>
              <p>Try a different search term</p>
            </div>
          )}
        </>
      ) : (
        <section className={styles.tasteSection} aria-label="Suggested people">
          <h2 className={styles.sectionTitle}>Suggested people</h2>

          {suggestionsLoading ? (
            <div className={styles.tasteSkeletonGrid}>
              <div className={styles.tasteSkeletonCard} />
              <div className={styles.tasteSkeletonCard} />
              <div className={styles.tasteSkeletonCard} />
            </div>
          ) : suggestedUsers.length > 0 ? (
            <div className={styles.tasteGrid}>
              {suggestedUsers.map((suggestedUser) => (
                <div key={suggestedUser._id} className={styles.tasteCard}>
                  <Link
                    to={`/user/${suggestedUser._id}`}
                    className={styles.tasteCardLink}
                  >
                    <div className={styles.tasteAvatar}>
                      {suggestedUser.avatar ? (
                        <img
                          src={getImageUrl(suggestedUser.avatar)}
                          alt=""
                        />
                      ) : (
                        <FaUser aria-hidden="true" />
                      )}
                    </div>
                    <div className={styles.tasteInfo}>
                      {(suggestedUser.firstName || suggestedUser.lastName) && (
                        <span className={styles.tasteName}>
                          {[suggestedUser.firstName, suggestedUser.lastName]
                            .filter(Boolean)
                            .join(" ")}
                        </span>
                      )}
                      {suggestedUser.username && (
                        <span
                          className={styles.tasteUsername}
                          title={`@${suggestedUser.username}`}
                        >
                          @{suggestedUser.username}
                        </span>
                      )}
                    </div>
                  </Link>
                  <button
                    type="button"
                    className={`${styles.tasteFollowBtn} ${
                      suggestedUser.isFollowing ? styles.tasteFollowing : ""
                    }`}
                    onClick={() => handleFollowSuggestedUser(suggestedUser._id)}
                  >
                    {suggestedUser.isFollowing ? (
                      <>
                        <FaUserCheck aria-hidden="true" /> Following
                      </>
                    ) : (
                      <>
                        <FaUserPlus aria-hidden="true" /> Follow
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.tasteEmpty}>
              No suggestions right now. Search for people above to find someone
              to follow.
            </p>
          )}
        </section>
      )}
        </div>
      </div>
    </div>
  );
};

export default UserSearchPage;
